import { useEffect, useState } from "react";
import { Star, ExternalLink, Quote } from "lucide-react";
import { toast } from "sonner";
import client from "../lib/api";

function Stars({ rating, size = 14 }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={size}
          fill={n <= rating ? "var(--onyx-gold)" : "transparent"}
          color={n <= rating ? "var(--onyx-gold)" : "var(--onyx-border-strong)"}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

export default function ReviewsSection() {
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ author_name: "", rating: 5, text: "", email: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = () => client.get("/reviews").then(r => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.author_name || !form.text) { toast.error("Name and review required"); return; }
    setSubmitting(true);
    try {
      await client.post("/reviews", form);
      toast.success("Thanks for sharing your experience!");
      setShowForm(false);
      setForm({ author_name: "", rating: 5, text: "", email: "" });
      load();
    } catch { toast.error("Failed to submit"); }
    finally { setSubmitting(false); }
  };

  if (!data) return null;
  const reviews = data.reviews.slice(0, 5);
  const summary = data.summary;

  return (
    <section data-testid="reviews-section" className="relative py-24 md:py-32 bg-white border-t border-black/5">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-16">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-6">
          <div>
            <span className="eyebrow">— Guest Stories</span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-4 leading-tight text-ink">What our <span className="text-ink">guests say</span></h2>
            <div className="flex items-center gap-4 mt-6">
              <div className="font-display text-5xl text-ink">{summary.avg_rating.toFixed(1)}</div>
              <div>
                <Stars rating={Math.round(summary.avg_rating)} size={18} />
                <p className="text-sm text-muted mt-1">Based on {summary.total_reviews}+ Google reviews</p>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              data-testid="write-google-review"
              href={summary.google_review_url}
              target="_blank" rel="noopener noreferrer"
              className="btn-luxe btn-luxe-gold !py-3"
            >
              Write a Google Review <ExternalLink size={14} />
            </a>
            <button
              data-testid="share-experience-btn"
              onClick={() => setShowForm(v => !v)}
              className="btn-luxe btn-luxe-ghost !py-3"
            >
              {showForm ? "Cancel" : "Share Your Experience"}
            </button>
          </div>
        </div>

        {/* On-site review form */}
        {showForm && (
          <form onSubmit={submit} data-testid="review-form" className="bg-cream border border-black/10 p-6 md:p-8 mb-12">
            <h3 className="font-display text-2xl text-ink mb-4">Tell us about your night</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-[11px] tracking-luxe-tight uppercase text-muted block mb-2">Your name *</label>
                <input data-testid="review-name" required className="luxe-input" value={form.author_name} onChange={e => setForm({ ...form, author_name: e.target.value })} />
              </div>
              <div>
                <label className="text-[11px] tracking-luxe-tight uppercase text-muted block mb-2">Email (optional)</label>
                <input data-testid="review-email" type="email" className="luxe-input" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="text-[11px] tracking-luxe-tight uppercase text-muted block mb-2">Rating *</label>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map(n => (
                    <button
                      key={n}
                      type="button"
                      data-testid={`star-${n}`}
                      onClick={() => setForm({ ...form, rating: n })}
                      className="p-1"
                    >
                      <Star size={28} fill={n <= form.rating ? "var(--onyx-gold)" : "transparent"} color={n <= form.rating ? "var(--onyx-gold)" : "var(--onyx-border-strong)"} strokeWidth={1.5} />
                    </button>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="text-[11px] tracking-luxe-tight uppercase text-muted block mb-2">Your experience *</label>
                <textarea data-testid="review-text" rows={4} required className="luxe-input resize-none" value={form.text} onChange={e => setForm({ ...form, text: e.target.value })} placeholder="Tell us what made your night memorable…" />
              </div>
            </div>
            <button data-testid="submit-review" type="submit" disabled={submitting} className="btn-luxe btn-luxe-solid mt-5">
              {submitting ? "Submitting…" : "Post Review"}
            </button>
            <p className="text-xs text-muted mt-3">Verified Onyx team reviews appear here. For your review to count toward our Google rating, please also post it on <a className="underline" href={summary.google_review_url} target="_blank" rel="noopener noreferrer">our Google listing</a>.</p>
          </form>
        )}

        {/* Reviews grid + featured venue photo */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10">
          {reviews.map((r, i) => (
            <div key={r.id || i} data-testid={`review-card-${i}`} className="bg-white p-8 hover:bg-cream transition-colors duration-500">
              <Quote className="text-gold mb-4" size={20} />
              <p className="text-ink leading-relaxed mb-6 line-clamp-5">{r.text}</p>
              <div className="flex items-center justify-between border-t border-black/10 pt-4">
                <div>
                  <p className="font-medium text-ink">{r.author_name}</p>
                  <Stars rating={r.rating} />
                </div>
                <span className="text-[9px] tracking-luxe uppercase text-muted">{r.source === "google" ? "Google" : "Verified Guest"}</span>
              </div>
            </div>
          ))}
          {/* Featured venue photo to fill the 6th slot */}
          <div data-testid="venue-feature-card" className="relative bg-black overflow-hidden min-h-[280px] group">
            <img
              src="https://customer-assets.emergentagent.com/job_onyx-premium-reserve/artifacts/nuxcpxyz_PHOTO-2025-10-06-10-39-37-4.webp"
              alt="Onyx Rooftop Lounge interior"
              className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-6">
              <p className="eyebrow text-[var(--onyx-gold-soft)]">— The Venue</p>
              <p className="font-display text-2xl text-white mt-2">Designed for the moment.</p>
              <p className="text-white/70 text-sm mt-2">1815 M Street NW · Washington, DC</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
